import React, { useRef, useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Horario semanal, dibujado como un calendario.
 *
 * Siete columnas, una por día, y una escala de horas a la izquierda. El hueco
 * entre dos bloques es el recreo, y se muestra como tal. Cada bloque se puede
 * borrar.
 *
 * Cómo se crea un bloque depende de con qué se esté usando, porque el
 * movimiento vertical no puede significar dos cosas a la vez:
 *   con mouse – se arrastra sobre la columna y el bloque queda del largo que se
 *               pintó;
 *   con dedo  – se toca y aparece un bloque de una hora, que después se estira
 *               de los tiradores. El arrastre vertical le queda al navegador
 *               para desplazar la grilla, que es lo que se hace todo el tiempo.
 *
 * Reemplaza al editor anterior, que pedía cada horario con cuatro desplegables
 * (hora y minutos de inicio, hora y minutos de fin). Un negocio abierto de lunes
 * a sábado llegaba a tener veinticuatro desplegables en pantalla al mismo
 * tiempo, y ninguno mostraba cómo quedaba el día.
 *
 * El dato que guarda es el mismo de siempre:
 *   { monday: { enabled: true, ranges: [{ start: '09:00', end: '13:00' }] }, ... }
 */

const PASO = 15;         // minutos que ocupa cada celda
const ALTO_CELDA = 11;   // px de alto por celda; una hora son 44px
const INICIO = 0;          // 00:00
const FIN = 24 * 60;       // 24:00
const RANURAS = (FIN - INICIO) / PASO;
// Alto de la fila de días, que es sticky y vive DENTRO del contenedor que
// scrollea: si no se la suma, la ventana recorta una hora del horario.
const ALTO_CABECERA = 60;

/**
 * Anchos mínimos de la grilla, en píxeles.
 *
 * Siete columnas repartiéndose el ancho de un celular no entran: medido en
 * Chrome, a 390px cada columna quedaba en 39px y el bloque en 26px, cuando
 * "09:00 – 18:00" necesita 68px. El resultado era el horario cortado en "09:0",
 * la palabra "Abierto" pisando la de la columna de al lado y "Cerrado"
 * desbordando su celda. Recién a 768px (90px por columna) entraba todo.
 *
 * Con un mínimo por columna, la semana completa nunca se comprime: si no entra,
 * la grilla se corre al costado, con la escala de horas y los días fijos. Es lo
 * mismo que hace una planilla de cálculo, y conserva un solo modo de uso en
 * todos los tamaños en vez de inventar una pantalla aparte para el celular.
 *
 * El mínimo cambia con el tamaño de pantalla, y las clases están escritas enteras
 * porque Tailwind solo genera lo que encuentra literal en el código:
 *
 *   hasta 1023px – 136px por día (grilla de 1008px). Ahí la grilla se desplaza
 *                  igual, así que columnas angostas no compran nada: mejor
 *                  aprovechar el ancho y que el bloque y el día se lean cómodos.
 *   desde 1024px – 112px por día (grilla de 840px). Es el techo con el que la
 *                  semana completa todavía entra sin desplazarse en una pantalla
 *                  de 1024px, como un Nest Hub. Si sobra ancho las columnas
 *                  crecen solas, porque el mínimo es un piso y no un tamaño.
 */
const ANCHO_ESCALA = 56;
const CLASE_MIN_DIA = 'min-w-[136px] lg:min-w-[112px]';
const CLASE_MIN_GRILLA = 'min-w-[1008px] lg:min-w-[840px]';

const aMinutos = (texto) => {
  const [h, m] = String(texto || '0:0').split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

/** 270 -> "4h 30m". Sirve para leer de un vistazo cuánto dura el bloque. */
const duracion = (minutos) => {
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
};

const aTexto = (min) => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/**
 * Ordena los rangos y fusiona los que se pisan o se tocan.
 *
 * Hace falta porque al pintar encima de un bloque existente se generarían dos
 * rangos superpuestos, y el backend calcula la disponibilidad recorriéndolos: si
 * se solapan, el mismo turno aparecería dos veces.
 */
const normalizar = (rangos) => {
  const ordenados = [...rangos]
    .filter((r) => r.fin > r.inicio)
    .sort((a, b) => a.inicio - b.inicio);

  const salida = [];
  for (const rango of ordenados) {
    const ultimo = salida[salida.length - 1];
    if (ultimo && rango.inicio <= ultimo.fin) {
      ultimo.fin = Math.max(ultimo.fin, rango.fin);
    } else {
      salida.push({ ...rango });
    }
  }
  return salida;
};

const aRangosDeMinutos = (dia) =>
  normalizar((dia?.ranges || []).map((r) => ({ inicio: aMinutos(r.start), fin: aMinutos(r.end) })));

const aRangosDeTexto = (rangos) =>
  rangos.map((r) => ({ start: aTexto(r.inicio), end: aTexto(r.fin) }));

export default function HorarioSemanal({ value, onChange, dayNames }) {
  const dias = Object.keys(dayNames);
  const contenedorRef = useRef(null);
  /**
   * El gesto en curso. Puede ser de tres tipos:
   *   pintar  – arrastrar sobre la columna vacía para crear un bloque
   *   mover   – arrastrar un bloque entero, sin cambiarle la duración
   *   inicio  – arrastrar el borde de arriba, para corregir la hora de entrada
   *   fin     – arrastrar el borde de abajo, para corregir la de salida
   *
   * Mientras dura el gesto los rangos viven acá y no en `value`: si se guardaran
   * en cada movimiento, `normalizar` fusionaría el bloque con el de al lado en
   * cuanto lo rozara y desaparecería en medio del arrastre. Se guarda al soltar.
   */
  const [gesto, setGesto] = useState(null);
  const gestoRef = useRef(null);

  const actualizarDia = (dia, cambios) => {
    onChange({ ...value, [dia]: { ...(value[dia] || {}), ...cambios } });
  };

  const guardarRangos = (dia, rangos) => {
    actualizarDia(dia, { enabled: rangos.length > 0, ranges: aRangosDeTexto(normalizar(rangos)) });
  };

  /**
   * Dónde cae una altura de pantalla dentro de la columna, medido en ranuras de
   * un cuarto de hora.
   *
   * Devuelve decimales a propósito: quien lo llama decide cómo redondear. Antes
   * redondeaba hacia abajo acá adentro, y como el resultado se vuelve a usar en
   * una resta contra el punto donde se agarró el tirador, el error de cada
   * redondeo se sumaba: un arrastre de 44px, que son exactamente cuatro ranuras,
   * podía terminar moviendo solo tres.
   */
  const posicionEnRanuras = (clientY, elementoColumna) => {
    const caja = elementoColumna.getBoundingClientRect();
    return Math.max(0, Math.min(RANURAS, (clientY - caja.top) / ALTO_CELDA));
  };

  const ranuraDesdeEvento = (evento, elementoColumna) =>
    posicionEnRanuras(evento.clientY, elementoColumna);

  const empezarGesto = (nuevo, evento, columna) => {
    // La columna y la última posición del dedo se guardan en el gesto porque el
    // seguimiento vive en `window` y no en la columna: así el arrastre sobrevive
    // a que React vuelva a dibujar el bloque en medio del movimiento, y sigue
    // andando aunque el dedo se salga de la grilla.
    gestoRef.current = { ...nuevo, columna, clientY: evento.clientY };
    setGesto(gestoRef.current);
    evento.currentTarget.setPointerCapture?.(evento.pointerId);
  };

  const alEmpezarAPintar = (evento, dia) => {
    // Solo botón principal, y no si el click salió de un bloque: ahí manda el
    // gesto del bloque (mover o estirar).
    if (evento.button !== 0 || evento.target.closest('[data-bloque]')) return;
    // Con el dedo no se pinta arrastrando: el movimiento vertical es el que usa
    // el navegador para desplazar la grilla, y no puede significar las dos cosas
    // a la vez. Ahí se toca para crear el bloque (ver `alTocarColumna`).
    if (evento.pointerType !== 'mouse') return;
    evento.preventDefault();

    const ranura = Math.floor(ranuraDesdeEvento(evento, evento.currentTarget));
    empezarGesto({ tipo: 'pintar', dia, desde: ranura, hasta: ranura }, evento, evento.currentTarget);
  };

  /**
   * Toque sobre una columna: crea un bloque de una hora donde se tocó.
   *
   * Es el reemplazo del arrastre en pantallas táctiles. Con el mouse no corre,
   * porque ahí el arrastre ya deja el bloque del largo que uno quiere.
   */
  const alTocarColumna = (evento, dia) => {
    if (evento.nativeEvent?.pointerType === 'mouse') return;
    if (evento.target.closest('[data-bloque]')) return;
    if (!value[dia]?.enabled) return;

    const ranura = Math.floor(ranuraDesdeEvento(evento, evento.currentTarget));
    const inicio = INICIO + ranura * PASO;
    guardarRangos(dia, [
      ...aRangosDeMinutos(value[dia]),
      { inicio, fin: Math.min(FIN, inicio + 60) },
    ]);
  };

  const alEmpezarConBloque = (evento, dia, indice, tipo) => {
    if (evento.button !== 0) return;
    // La cruz de borrar no arrastra nada.
    if (evento.target.closest('[data-borrar]')) return;
    // Mover el bloque entero es solo con mouse. Para hacerlo con el dedo habría
    // que reservarle a la app todo el movimiento vertical sobre el bloque, y un
    // bloque de nueve horas tapa casi toda la columna: se perdería el
    // desplazamiento de la grilla justo donde más se toca. Con el dedo se
    // estira de los tiradores, que ocupan una franja finita.
    if (tipo === 'mover' && evento.pointerType !== 'mouse') return;
    evento.preventDefault();
    evento.stopPropagation();

    const rangos = aRangosDeMinutos(value[dia]);
    const rango = rangos[indice];
    const columna = evento.currentTarget.closest('[data-columna]');
    const ranura = ranuraDesdeEvento(evento, columna);

    // A qué distancia del borde que se va a mover quedó el puntero. Sin esto el
    // borde salta a la posición del puntero apenas se lo toca: como el tirador
    // mide 14px y se agarra por el medio, estirar hacia abajo empezaba
    // acortando el bloque un cuarto de hora, y un arrastre de 44px (una hora)
    // terminaba dando media. El borde de referencia es el de abajo cuando se
    // estira el final, y el de arriba en los demás casos.
    const ranuraDelBorde =
      tipo === 'fin' ? (rango.fin - INICIO) / PASO : (rango.inicio - INICIO) / PASO;

    empezarGesto(
      {
        tipo,
        dia,
        indice,
        rangos,
        agarre: ranura - ranuraDelBorde,
        duracion: rango.fin - rango.inicio,
      },
      evento,
      columna
    );
  };

  /**
   * Recalcula el gesto para una posición vertical del puntero.
   *
   * Recibe la coordenada suelta —y no el evento— porque la llaman dos cosas: el
   * seguimiento del puntero y el desplazamiento automático del borde, que
   * necesita rehacer la cuenta sin que el dedo se haya movido.
   */
  const aplicarMovimiento = useCallback((clientY) => {
    const g = gestoRef.current;
    if (!g || !g.columna) return;

    const ranura = posicionEnRanuras(clientY, g.columna);
    let actualizado;

    if (g.tipo === 'pintar') {
      actualizado = { ...g, hasta: Math.min(Math.floor(ranura), RANURAS - 1) };
    } else {
      const rangos = g.rangos.map((r) => ({ ...r }));
      const rango = rangos[g.indice];
      // Se descuenta dónde se agarró el tirador, así el borde acompaña al
      // puntero en vez de saltar debajo de él.
      const minuto = INICIO + Math.round(ranura - g.agarre) * PASO;

      if (g.tipo === 'mover') {
        let nuevoInicio = INICIO + Math.round(ranura - g.agarre) * PASO;
        // Que no se escape por arriba ni por abajo del día.
        nuevoInicio = Math.max(INICIO, Math.min(FIN - g.duracion, nuevoInicio));
        rango.inicio = nuevoInicio;
        rango.fin = nuevoInicio + g.duracion;
      } else if (g.tipo === 'inicio') {
        // Nunca por debajo de un cuarto de hora de duración.
        rango.inicio = Math.max(INICIO, Math.min(minuto, rango.fin - PASO));
      } else {
        rango.fin = Math.min(FIN, Math.max(minuto, rango.inicio + PASO));
      }

      actualizado = { ...g, rangos };
    }

    gestoRef.current = { ...actualizado, clientY };
    setGesto(gestoRef.current);
  }, []);

  const alSoltar = useCallback(() => {
    const g = gestoRef.current;
    gestoRef.current = null;
    setGesto(null);
    if (!g) return;

    if (g.tipo === 'pintar') {
      const desde = Math.min(g.desde, g.hasta);
      const hasta = Math.max(g.desde, g.hasta);
      const nuevo = {
        inicio: INICIO + desde * PASO,
        // +1 para que un click simple deje un bloque de una ranura y no uno vacío.
        fin: INICIO + (hasta + 1) * PASO,
      };
      guardarRangos(g.dia, [...aRangosDeMinutos(value[g.dia]), nuevo]);
    } else {
      guardarRangos(g.dia, g.rangos);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    // Si se suelta el mouse fuera de la grilla, el arrastre tiene que terminar
    // igual: si no, queda pintando al volver a entrar sin haber apretado nada.
    window.addEventListener('pointerup', alSoltar);
    window.addEventListener('pointercancel', alSoltar);
    return () => {
      window.removeEventListener('pointerup', alSoltar);
      window.removeEventListener('pointercancel', alSoltar);
    };
  }, [alSoltar]);

  const hayGesto = Boolean(gesto);

  useEffect(() => {
    if (!hayGesto) return;

    const seguir = (evento) => aplicarMovimiento(evento.clientY);
    window.addEventListener('pointermove', seguir);

    /**
     * Desplaza la grilla sola cuando el puntero llega a un borde.
     *
     * Sin esto solo se puede estirar un bloque hasta donde ya se ve, porque
     * mientras se arrastra no hay forma de desplazar. Medido en un teléfono:
     * arrastrando 88px hacia abajo el bloque crecía 45 minutos en vez de dos
     * horas, porque el borde de la grilla lo frenaba. En un monitor no se nota
     * porque sobra altura.
     *
     * Va por temporizador y no dentro del seguimiento del puntero: con el dedo
     * quieto contra el borde no llegan más eventos de movimiento, y es
     * justamente ahí donde tiene que seguir corriendo.
     */
    const MARGEN = 44;
    const SALTO = 10;
    const tic = setInterval(() => {
      const g = gestoRef.current;
      const contenedor = contenedorRef.current;
      if (!g || !contenedor || g.clientY == null) return;

      const caja = contenedor.getBoundingClientRect();
      let salto = 0;
      if (g.clientY > caja.bottom - MARGEN) salto = SALTO;
      else if (g.clientY < caja.top + MARGEN) salto = -SALTO;
      if (!salto) return;

      const antes = contenedor.scrollTop;
      contenedor.scrollTop = antes + salto;
      // Si ya está en el tope no hay nada que recalcular.
      if (contenedor.scrollTop !== antes) aplicarMovimiento(g.clientY);
    }, 40);

    return () => {
      window.removeEventListener('pointermove', seguir);
      clearInterval(tic);
    };
  }, [hayGesto, aplicarMovimiento]);

  useEffect(() => {
    // La grilla muestra el día completo, así que al abrirla se vería la
    // madrugada, que no le sirve a nadie. Arranca posicionada media hora antes
    // del primer bloque del negocio.
    //
    // Solo al montar: si corriera con cada cambio, saltaría el scroll cada vez
    // que se pinta un bloque.
    const contenedor = contenedorRef.current;
    if (!contenedor) return;

    let primero = Infinity;
    dias.forEach((dia) => {
      (value?.[dia]?.ranges || []).forEach((r) => {
        primero = Math.min(primero, aMinutos(r.start));
      });
    });

    const desde = Number.isFinite(primero) ? primero - 30 : 8 * 60;
    contenedor.scrollTop = Math.max(0, ((desde - INICIO) / PASO) * ALTO_CELDA);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const borrarBloque = (dia, indice) => {
    const rangos = aRangosDeMinutos(value[dia]).filter((_, i) => i !== indice);
    guardarRangos(dia, rangos);
  };

  const alternarDia = (dia, abierto) => {
    if (abierto) {
      const rangos = aRangosDeMinutos(value[dia]);
      actualizarDia(dia, {
        enabled: true,
        // Un día que se abre sin horario cargado arranca con una jornada
        // razonable en vez de vacío, para no obligar a pintar desde cero.
        ranges: rangos.length ? aRangosDeTexto(rangos) : [{ start: '09:00', end: '18:00' }],
      });
    } else {
      actualizarDia(dia, { enabled: false });
    }
  };

  const horas = [];
  for (let m = INICIO; m <= FIN; m += 60) horas.push(m);

  /**
   * Alto de la ventana de la grilla.
   *
   * Era `100dvh - 300px` a secas, o sea que la ventana la decidía el tamaño del
   * monitor y no el horario: un negocio de 09:00 a 20:00 ocupa 11h (484px) y en
   * una pantalla de 900px la ventana medía 600, así que quedaban 2 horas y media
   * de grilla vacía debajo del último bloque, adentro de la tarjeta. En un
   * monitor más alto, más.
   *
   * Ahora sale de lo que hay cargado: desde una hora antes del primer bloque
   * hasta una hora después del último. Esa hora de aire a cada lado no es
   * decorativa — es lo que deja ver que hay grilla para seguir y se puede
   * arrastrar hacia afuera sin tener que scrollear primero.
   *
   * Sigue siendo un `max-height` sobre las 24 horas completas, no un recorte:
   * el contenido de la grilla no cambia, así que scrolleando se llega igual a
   * cualquier hora y un bloque se puede estirar hasta las 23:00 en un negocio
   * que hoy cierra a las 18:00. Lo único que cambia es cuánto se ve sin
   * scrollear.
   *
   * El techo anterior se conserva: en una pantalla baja, o con un horario que
   * ocupa casi todo el día, manda `100dvh - 300px`.
   */
  const altoDeLaVentana = (() => {
    let primero = Infinity;
    let ultimo = -Infinity;
    dias.forEach((dia) => {
      (value?.[dia]?.ranges || []).forEach((r) => {
        primero = Math.min(primero, aMinutos(r.start));
        ultimo = Math.max(ultimo, aMinutos(r.end));
      });
    });
    // Sin nada cargado no hay horario del que salir: se deja el techo de antes.
    if (!Number.isFinite(primero) || !Number.isFinite(ultimo)) {
      return 'max(240px, calc(100dvh - 300px))';
    }
    const desde = Math.max(INICIO, primero - 60);
    const hasta = Math.min(FIN, ultimo + 60);
    const alto = Math.round(((hasta - desde) / PASO) * ALTO_CELDA) + ALTO_CABECERA;
    return `min(${alto}px, max(240px, calc(100dvh - 300px)))`;
  })();

  return (
    <div className="w-full">
      <p className="mb-3 text-sm text-neutral-600">
        Tocá o arrastrá sobre un día para marcar cuándo atendés, y estirá el bloque de los bordes
        para ajustarlo. El hueco entre dos bloques queda libre.
      </p>

      {/* Un solo marco que se desplaza en los dos ejes, como una planilla: los
          días quedan fijos arriba y la escala de horas fija a la izquierda, así
          que por más que se corra nunca se pierde la referencia de qué día ni
          de qué hora se está mirando.

          Antes eran dos cajas: la cabecera fija y la grilla con desplazamiento
          vertical propio. Con esa forma no hay manera de correr las dos a la
          par en horizontal, y sin correrlas los siete días tenían que caber sí
          o sí en el ancho disponible. */}
      {/* El alto disponible no es un porcentaje de la pantalla: es lo que sobra
          después de restar lo de arriba y lo de abajo, que es fijo en píxeles.
          Medido en Chrome sobre la zona de contenido: por encima de la grilla
          hay entre 171px (escritorio) y 237px (celular) de título y ayuda, más
          la cabecera del onboarding y la navegación.

          Los 480px de antes estaban calibrados para el layout anterior, en el
          que scrolleaba la página entera; con el actual sobraban entre 55 y
          160px vacíos debajo de la grilla según el tamaño. Con 300 el resto baja
          a 10-60px, que es el aire que igual conviene dejar al pie. */}
      <div
        className="overflow-auto rounded-2xl border border-neutral-200 bg-white"
        ref={contenedorRef}
        style={{ maxHeight: altoDeLaVentana }}
      >
        <div className={CLASE_MIN_GRILLA}>
        {/* Cabecera con los días */}
        <div className="sticky top-0 z-20 flex border-b border-neutral-200 bg-neutral-50">
          <div
            className="sticky left-0 z-10 shrink-0 bg-neutral-50"
            style={{ width: ANCHO_ESCALA }}
          />
          {dias.map((dia) => {
            const abierto = Boolean(value[dia]?.enabled);
            return (
              <div
                key={dia}
                className={cn('flex flex-1 items-center gap-1 px-1.5 py-2', CLASE_MIN_DIA)}
              >
                {/* El día entero es el botón que abre y cierra.
                    Antes era el nombre del día como texto muerto y, debajo, una
                    casilla de 12px con la palabra "Abierto" al lado: en un
                    teléfono había que apuntarle a una casilla más chica que la
                    yema de un dedo. Ahora se toca el día y listo, con 44px de
                    alto, que es el mínimo que piden Apple y Google.

                    El nombre adentro del botón es también lo que lo hace
                    inequívoco: un botón que dijera "Abre"/"Cierra" se lee como
                    la acción y no como el estado —no se sabe si "Cierra"
                    significa "está cerrado" o "tocá para cerrarlo"—. Diciendo
                    "Lun" y "Abierto", con el relleno del color de la marca, el
                    estado se ve sin leer nada. */}
                {/* Se ve apretable de verdad: relieve, se hunde al tocarlo y se
                    despega al pasarle el mouse por encima. Antes era una caja
                    plana del mismo color que la cabecera y no se leía como algo
                    que se pudiera apretar; ahora el estado abierto va relleno y
                    con sombra, y el cerrado hundido y sin relieve. */}
                <button
                  type="button"
                  onClick={() => alternarDia(dia, !abierto)}
                  aria-pressed={abierto}
                  aria-label={`${dayNames[dia]}: ${abierto ? 'abierto, tocá para cerrar' : 'cerrado, tocá para abrir'}`}
                  className={cn(
                    'flex min-h-[44px] min-w-0 flex-1 flex-col items-center justify-center rounded-xl border leading-tight',
                    'transition-all duration-150 active:translate-y-px active:shadow-none',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-1',
                    abierto
                      ? 'border-primary bg-primary text-primary-foreground shadow-sm hover:-translate-y-px hover:shadow-md'
                      : 'border-neutral-300 bg-neutral-100 text-neutral-500 shadow-inner hover:bg-neutral-200 hover:text-neutral-700'
                  )}
                >
                  <span className="truncate text-sm font-bold">
                    {dayNames[dia].slice(0, 3)}
                  </span>
                  <span className="truncate text-[10px] font-medium opacity-90">
                    {abierto ? 'Abierto' : 'Cerrado'}
                  </span>
                </button>
              </div>
            );
          })}
        </div>


        {/* El alto máximo lo pone el marco de afuera: NO es un porcentaje de la
            pantalla sino lo que sobra después de restar lo de arriba y lo de
            abajo, que es fijo en píxeles. Con 44vh, en una ventana de 1080px la
            grilla usaba 475px de los 597 disponibles —122 desperdiciados— y en
            una de 700px pedía 308 cuando solo entraban 230. */}
        <div className="flex">
          {/* Escala de horas */}
          {/* Las etiquetas van posicionadas, no como filas apiladas.
              Antes eran 18 filas de 4 celdas cada una: 18 x 44px = 792px contra
              los 68 x 11px = 748px que miden las columnas. Esos 44px de más
              eran el espacio vacío que quedaba abajo de la caja. */}
          <div
            className="sticky left-0 z-10 shrink-0 bg-white"
            style={{ width: ANCHO_ESCALA, height: RANURAS * ALTO_CELDA }}
          >
            {horas.map((m) => (
              <span
                key={m}
                className="absolute right-2.5 -translate-y-1/2 text-[11px] tabular-nums text-neutral-400"
                style={{ top: ((m - INICIO) / PASO) * ALTO_CELDA }}
              >
                {aTexto(m)}
              </span>
            ))}
          </div>

          {dias.map((dia) => {
            const abierto = Boolean(value[dia]?.enabled);
            const gestoDelDia = gesto?.dia === dia ? gesto : null;
            // Mientras se mueve o se estira un bloque se dibuja el estado del
            // gesto, que todavía no se guardó.
            const rangos =
              gestoDelDia && gestoDelDia.tipo !== 'pintar'
                ? gestoDelDia.rangos
                : aRangosDeMinutos(value[dia]);
            const arrastre = gestoDelDia?.tipo === 'pintar' ? gestoDelDia : null;

            return (
              <div
                key={dia}
                data-columna
                onPointerDown={abierto ? (e) => alEmpezarAPintar(e, dia) : undefined}
                className={cn(
                  'relative flex-1 border-l border-neutral-100',
                  CLASE_MIN_DIA,
                  abierto ? 'cursor-crosshair bg-white' : 'bg-neutral-50'
                )}
                onClick={(e) => alTocarColumna(e, dia)}
                style={{ height: RANURAS * ALTO_CELDA }}
              >
                {/* Líneas de cada hora */}
                {horas.slice(1).map((m, i) => (
                  <div
                    key={m}
                    className="pointer-events-none absolute inset-x-0 border-t border-neutral-100"
                    style={{ top: (i + 1) * 4 * ALTO_CELDA }}
                  />
                ))}

                {!abierto && (
                  <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-[11px] text-neutral-400">
                    Cerrado
                  </span>
                )}

                {abierto &&
                  rangos.map((rango, indice) => {
                    const top = ((rango.inicio - INICIO) / PASO) * ALTO_CELDA;
                    const alto = ((rango.fin - rango.inicio) / PASO) * ALTO_CELDA;
                    const anterior = rangos[indice - 1];
                    // Los tiradores no pueden ser siempre de 14px: en un bloque
                    // de un cuarto de hora (11px de alto) dos tiradores de 14 se
                    // superponen y tapan el bloque entero, así que no quedaría
                    // forma de agarrarlo para moverlo. Se limitan a un tercio
                    // del alto, con 6px de piso para que sigan siendo apuntables.
                    const altoTirador = Math.max(6, Math.min(14, Math.round(alto / 3)));

                    return (
                      /* La clave es la posición y no las horas del bloque: con
                         las horas, cada cuarto de hora de arrastre cambiaba la
                         clave, React desmontaba el bloque y montaba otro, y con
                         él se perdía el elemento que tenía capturado el puntero.
                         Durante un gesto el orden no cambia, así que la posición
                         alcanza. */
                      <React.Fragment key={indice}>
                        {/* El hueco con el bloque anterior es el recreo. Se
                            dibuja para que se entienda que es a propósito y no
                            un olvido. */}
                        {anterior && rango.inicio > anterior.fin && (
                          <div
                            className="pointer-events-none absolute inset-x-1.5 flex items-center justify-center rounded-lg border border-dashed border-neutral-300 bg-neutral-50/80"
                            style={{
                              top: ((anterior.fin - INICIO) / PASO) * ALTO_CELDA,
                              height: ((rango.inicio - anterior.fin) / PASO) * ALTO_CELDA,
                            }}
                          >
                            <span className="truncate px-1 text-[9px] text-neutral-400">Recreo</span>
                          </div>
                        )}

                        <div
                          data-bloque
                          onPointerDown={(e) => alEmpezarConBloque(e, dia, indice, 'mover')}
                          className={cn(
                            'group absolute inset-x-1.5 overflow-hidden rounded-lg bg-primary text-primary-foreground shadow-sm',
                            gestoDelDia?.indice === indice && gestoDelDia?.tipo !== 'pintar'
                              ? 'cursor-grabbing ring-2 ring-primary/50'
                              : 'cursor-grab'
                          )}
                          style={{ top, height: alto }}
                        >
                          {/* Tiradores para corregir la hora de entrada y la de
                              salida sin borrar el bloque y volver a pintarlo.

                              `touch-action: none` solo acá: es lo que le saca al
                              navegador el movimiento vertical para que el dedo
                              pueda estirar en vez de desplazar. Puesto sobre el
                              bloque entero rompería el desplazamiento de la
                              grilla, porque un bloque largo tapa casi toda la
                              columna; en una franja de 14px no molesta.

                              La barrita blanca no es decoración: sin algo
                              visible no hay forma de adivinar que el borde se
                              puede estirar, que es justo lo que hay que hacer
                              después de tocar para crear el bloque. */}
                          <div
                            onPointerDown={(e) => alEmpezarConBloque(e, dia, indice, 'inicio')}
                            title="Arrastrá para cambiar la hora de entrada"
                            style={{ touchAction: 'none', height: altoTirador }}
                            className="absolute inset-x-0 top-0 flex cursor-ns-resize items-center justify-center hover:bg-white/25"
                          >
                            {alto >= 44 && <span className="h-0.5 w-6 rounded-full bg-white/70" />}
                          </div>
                          <div
                            onPointerDown={(e) => alEmpezarConBloque(e, dia, indice, 'fin')}
                            title="Arrastrá para cambiar la hora de salida"
                            style={{ touchAction: 'none', height: altoTirador }}
                            className="absolute inset-x-0 bottom-0 flex cursor-ns-resize items-center justify-center hover:bg-white/25"
                          >
                            {alto >= 44 && <span className="h-0.5 w-6 rounded-full bg-white/70" />}
                          </div>
                          {/* El texto arranca debajo del tirador de arriba: con
                              menos separación, la barrita del tirador cruzaba
                              justo la línea del horario y lo dejaba tachado.
                              Por eso también sube el umbral de las dos líneas,
                              de 34 a 44: son los 14px del tirador más las dos
                              líneas de texto. 44px es justo el alto de un bloque
                              de una hora, que es el que deja un toque, así que
                              recién creado ya muestra su horario. */}
                          {/* `pointer-events-none` en los textos: sin eso se
                              quedan con el toque y tapan al tirador de abajo.
                              Medido sobre un bloque de una hora (44px): el
                              tirador ocupaba bien sus 14px, de 30 a 44, pero el
                              texto llegaba hasta los 41,5 y le ganaba, así que
                              quedaban 2px agarrables. En un bloque largo no se
                              notaba porque el texto vive arriba y el tirador de
                              abajo queda despejado. */}
                          {alto >= 44 ? (
                            <>
                              <span className="pointer-events-none block px-2.5 pt-3.5 text-[11px] font-semibold leading-tight tabular-nums">
                                {aTexto(rango.inicio)} – {aTexto(rango.fin)}
                              </span>
                              <span className="pointer-events-none block px-2.5 text-[11px] leading-tight opacity-85">
                                {duracion(rango.fin - rango.inicio)}
                              </span>
                            </>
                          ) : (
                            <span className="pointer-events-none block px-2.5 text-[11px] font-semibold leading-tight tabular-nums">
                              {duracion(rango.fin - rango.inicio)}
                            </span>
                          )}
                          {/* El stopPropagation no es opcional: sin él, el
                              pointerdown sube al bloque, que arranca su gesto,
                              hace preventDefault y captura el puntero, así que el
                              click nunca llega al botón. Y aunque llegara, el
                              pointerup del gesto volvería a guardar el bloque
                              recién borrado. */}
                          {/* Visible siempre, salvo en equipos con mouse, donde
                              aparece al pasar por encima. Estaba escondido hasta
                              el hover, y en una pantalla táctil el hover no
                              existe: con el dedo no había manera de borrar un
                              bloque. */}
                          <button
                            type="button"
                            data-borrar
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={() => borrarBloque(dia, indice)}
                            aria-label={`Borrar el bloque de ${aTexto(rango.inicio)} a ${aTexto(rango.fin)} de ${dayNames[dia]}`}
                            className="absolute right-1.5 top-1.5 block rounded-full bg-white p-1 text-red-600 shadow-sm transition-colors hover:bg-red-600 hover:text-white [@media(hover:hover)]:hidden [@media(hover:hover)]:group-hover:block"
                          >
                            <X className="h-3 w-3" strokeWidth={3} />
                          </button>
                        </div>
                      </React.Fragment>
                    );
                  })}

                {/* Lo que se está pintando ahora mismo */}
                {arrastre && (
                  <div
                    className="pointer-events-none absolute inset-x-1.5 rounded-lg bg-primary/40 ring-1 ring-primary"
                    style={{
                      top: Math.min(arrastre.desde, arrastre.hasta) * ALTO_CELDA,
                      height: (Math.abs(arrastre.hasta - arrastre.desde) + 1) * ALTO_CELDA,
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
        </div>
      </div>
    </div>
  );
}
